import { Prerequisite } from '@/jbi-shared/dto/jtms.dtos';
import { DateOrString } from '@/jbi-shared/types/full-document-revision-object.types';
import { CourseModuleStatus } from '@/jbi-shared/types/jtms.types';
import { ApiState } from '@/store/types/general.types';
import { generateTempId } from '@/utils/jtms-utils';
import { ModuleResponseDTO } from '../module/module.types';
import { TrainerResponseDTO } from '../trainer/trainer.types';

// Placeholder interface for future implementations
export interface CourseModuleApiState {
  createCourseModulesApi: ApiState; // added to prevent linter errors
  updateCourseModulesApi: ApiState; // added to prevent linter errors
  deleteCourseModulesApi: ApiState; // added to prevent linter errors
}

export interface CourseModuleState {
  apiState: CourseModuleApiState;
  courseModulesTabState: CourseModulesTabState;
}

export interface CreateCourseModuleDTO {
  courseId?: number;
  moduleComponents: CourseModuleModuleComponent[];
}

export interface CourseModulesTabState {
  activeCourseModules: CourseModuleComponentItem[];
  deletedCourseModules: CourseModuleComponentItem[];
  courseModuleFormHasError: boolean;
}

/**
 * Separated module component for `CreateCourseModuleDTO`.
 *
 * Course modules are essentially a mapping between courses and modules.
 * A course module may contain several modules (in turn several course
 * modules), so the 'module' part can be distilled into a separate interface
 * for better reusability.
 */
export interface CourseModuleModuleComponent {
  // optional id property for existing components
  id?: number;
  // Temp id used for establishing temporary relations between course module objects
  tempId: number;
  moduleId: number;
  trainerIds: number[];
  startDate: DateOrString;
  endDate: DateOrString;
  prerequisiteIds: number[];
  note?: string;
}

export interface CourseModulePrerequisiteDTO {
  courseModuleId: number;
  prerequisiteId: number;
}

export interface CourseModuleBase {
  id?: number | null; // Assuming ID can be null initially on the frontend
  module?: ModuleResponseDTO | null;
  trainers?: TrainerResponseDTO[] | null;
  startDate?: DateOrString | null;
  endDate?: DateOrString | null;
  status: CourseModuleStatus;
  note?: string;
}

export interface CourseModuleResponseDTO extends CourseModuleBase {
  prerequisites?: CourseModuleResponseDTO[]; // DTO-specific nesting
}

// Component-specific nesting for FRONTEND
export interface CourseModuleComponentItem extends CourseModuleBase {
  prerequisiteIds?: Prerequisite[];
  // Label is used for frontend display only
  label?: string;
  // The properties below are explicitly used for checking circular dependencies between course modules in FRONTEND
  requiredByIds?: number[];
  tempId?: number;
  hasFieldError?: boolean;
  shouldDisablePrerequisiteDropdown?: boolean;
}

export interface UpdateCourseModulePayload {
  index: number;
  data: Partial<CourseModuleComponentItem>;
}

export interface UpdateCourseModulePrerequisitePayload {
  index: number;
  data: Prerequisite[];
}

export interface IndexBooleanPayload {
  index: number;
  data: boolean;
}

export const defaultCourseModule = (): CourseModuleComponentItem => ({
  tempId: generateTempId(),
  module: null,
  trainers: [],
  startDate: null,
  endDate: null,
  prerequisiteIds: [],
  requiredByIds: [],
  status: CourseModuleStatus.ACTIVE,
  note: '',
  hasFieldError: true,
  shouldDisablePrerequisiteDropdown: false
});

export interface CourseModuleDropdownSelection {
  id: number;
  label: string;
}

export interface UpdateCourseModulesDTO {
  id?: number;
  moduleId?: number;
  trainerIds?: number[];
  prerequisiteIds?: number[];
  startDate: DateOrString | null;
  endDate: DateOrString | null;
  note?: string;
}

export interface CreateCourseModulesApiDTO {
  courseId: number;
  courseModuleTempId: number;
}

export interface UpdateCourseModuleApiDTO {
  courseId: number;
  courseModulesToUpdate: UpdateCourseModulesDTO[];
}

export interface DeleteCourseModulesApiDTO {
  courseId: number;
  courseModuleIdsToDelete: number[];
}

export interface CreateCourseModuleResponse {
  tempId?: number;
  realId: number;
  createdCourseModule: CourseModuleResponseDTO;
}
